import React, { useRef, useEffect } from "react";

const WIREFRAME_COLORS = ["#3d3550", "#5a4f72", "#6b5b8a"];

// Animus wireframe lattice — muted purple-gray, no bright nodes
export default function PlexusBackground({ className = "", style = {} }) {
  const canvasRef = useRef(null);
  const animationRef = useRef();

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    let width = canvas.width = canvas.offsetWidth;
    let height = canvas.height = canvas.offsetHeight;

    const GRID = 64;
    let offsetX = 0;
    let offsetY = 0;

    const polygons = Array.from({ length: 4 }, () => ({
      cx: Math.random() * width,
      cy: Math.random() * height,
      radius: 80 + Math.random() * 120,
      sides: 5 + Math.floor(Math.random() * 3),
      rotation: Math.random() * Math.PI,
      speed: 0.0003 + Math.random() * 0.0004,
      color: WIREFRAME_COLORS[Math.floor(Math.random() * WIREFRAME_COLORS.length)],
    }));

    const handleResize = () => {
      width = canvas.width = canvas.offsetWidth;
      height = canvas.height = canvas.offsetHeight;
    };
    window.addEventListener("resize", handleResize);

    function drawGrid(breath) {
      ctx.strokeStyle = `rgba(90, 79, 114, ${0.06 * breath})`;
      ctx.lineWidth = 1;

      const startX = -GRID + (offsetX % GRID);
      const startY = -GRID + (offsetY % GRID);

      for (let x = startX; x < width + GRID; x += GRID) {
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, height);
        ctx.stroke();
      }
      for (let y = startY; y < height + GRID; y += GRID) {
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(width, y);
        ctx.stroke();
      }
    }

    function drawPolygons(breath) {
      for (const poly of polygons) {
        poly.rotation += poly.speed;
        ctx.save();
        ctx.translate(poly.cx, poly.cy);
        ctx.rotate(poly.rotation);
        ctx.beginPath();
        for (let i = 0; i <= poly.sides; i++) {
          const angle = (i / poly.sides) * Math.PI * 2;
          const px = Math.cos(angle) * poly.radius;
          const py = Math.sin(angle) * poly.radius;
          if (i === 0) ctx.moveTo(px, py);
          else ctx.lineTo(px, py);
        }
        ctx.closePath();
        ctx.strokeStyle = poly.color;
        ctx.globalAlpha = 0.12 * breath;
        ctx.lineWidth = 1;
        ctx.stroke();
        ctx.restore();
      }
    }

    let t = 0;
    function animate() {
      ctx.clearRect(0, 0, width, height);
      t += 0.004;
      const breath = 0.85 + 0.15 * Math.sin(t);
      offsetX += 0.15;
      offsetY += 0.08;

      drawGrid(breath);
      drawPolygons(breath);

      animationRef.current = requestAnimationFrame(animate);
    }
    animate();

    return () => {
      cancelAnimationFrame(animationRef.current);
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className={"plexus-bg-canvas " + className}
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        zIndex: 0,
        pointerEvents: "none",
        ...style,
      }}
      aria-hidden="true"
    />
  );
}
